"use client";
import { useEffect, useMemo, useState } from "react";
import {
  useDistrictFetchData,
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useFetchCountryWarehouseAllData,
} from "../../../apis/locationApis";
import {
  useFetchStaffsAllData,
  useOutletRolesData,
  useRolesData,
} from "../../../apis/staffApis";
import { Control, Controller, UseFormClearErrors } from "react-hook-form";
import MultiSelectInput from "../../../components/MultiSelectInput";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import { getFileExtension } from "../../../utilities/helper";
import {
  IconCloudUpload,
  IconPhoto,
  IconTrash,
  IconCheck,
  IconBuilding,
  IconMapPin,
  IconBriefcase,
  IconId,
  IconCalendar,
  IconUser,
  IconPhone,
  IconMail,
  IconLock,
} from "@tabler/icons-react";
import { useFetchStaffsTimeSlotAllData } from "../../../apis/timeSlotApis";
import { useSelector } from "react-redux";
import { useFetchDarkStoreAllData } from "../../../apis/darkStoreApi";
import { useFetchAllOutlets } from "../../../apis/outletApis";
import { useLanguage } from "../../../context/LanguageContext";

interface LocationOption {
  id: string;
  name: string;
  type: "warehouse" | "outlet";
  cityId: string;
  districtId: string;
  countryId: string;
  cityName: string;
  districtName: string;
  address: string;
  shortAddress?: string;
}

type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors: UseFormClearErrors<any>;
  control: Control<any>;
  setSelectedDocument?: any;
  getIds?: any;
  staffDetails?: any;
};

const AddStaffModal = ({
  register,
  errors,
  setValue,
  watch,
  getIds,
  control,
  clearErrors,
  setSelectedDocument,
  staffDetails,
}: Props) => {
  const { t } = useLanguage();
  const profile = useSelector((state: any) => state.auth.profile);
  const isClient = profile?.type === "client";
  const isDSClient = profile?.clientType === "DS";

  const [selectedData, setSelectedData] = useState<any>({
    warehouse: "",
    outlet: "",
    country: "",
    city: "",
    district: "",
  });

  const [createLocation, setCreateLocation] = useState<any>({});

  const { data: staffData, refetch: fetchStaffs } = useFetchStaffsAllData();
  const { data: timeSlotData, refetch: fetchTimeSlot } =
    useFetchStaffsTimeSlotAllData();
  const { data: warehouseData, refetch: fetchWarehouses } =
    useFetchCountryWarehouseAllData();
  const { data: outletData, refetch: fetchOutlets } = useFetchAllOutlets();
  const { data: warehouseRoles, refetch: fetchWarehouseRoles } = useRolesData();
  const { data: outletRoles, refetch: fetchOutletRoles } = useOutletRolesData();
  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: darkStores } = useFetchDarkStoreAllData();

  const {
    data: cities = [],
    refetch: fetchReceiverCities,
    isFetching: isFetchingCities,
  } = useFetchCountryCitiesAllData(createLocation?.country?.id);

  const {
    data: districts = [],
    refetch: fetchReceiverDistricts,
    isFetching: isFetchingDistricts,
  } = useDistrictFetchData(createLocation?.city?.id);

  useEffect(() => {
    fetchStaffs();
    fetchWarehouses();
    fetchOutlets();
    fetchTimeSlot();
    fetchWarehouseRoles();
    fetchOutletRoles();
  }, [
    fetchStaffs,
    fetchWarehouses,
    fetchOutlets,
    fetchTimeSlot,
    fetchWarehouseRoles,
    fetchOutletRoles,
  ]);

  useEffect(() => {
    if (createLocation?.country?.id) fetchReceiverCities();
  }, [createLocation?.country?.id, fetchReceiverCities]);

  useEffect(() => {
    if (createLocation?.city?.id) fetchReceiverDistricts();
  }, [createLocation?.city?.id, fetchReceiverDistricts]);

  useEffect(() => {
    if (selectedData) getIds(selectedData);
  }, [selectedData, getIds]);

  const combinedLocationOptions: LocationOption[] = useMemo(() => {
    const warehouses: LocationOption[] = (warehouseData?.warehouses || []).map(
      (w: any): LocationOption => ({
        id: w.id,
        name: w.name,
        type: "warehouse",
        cityId: w.city,
        districtId: w.district,
        countryId: w.country,
        cityName: w.cityName || "",
        districtName: w.districtName || "",
        address: w.location || "",
        shortAddress: w.shortAddress || "",
      }),
    );

    const outlets: LocationOption[] = (outletData?.outlets || [])
      .filter((o: any) => o.status === "ACTIVE")
      .map(
        (o: any): LocationOption => ({
          id: o.id,
          name: o.outletName,
          type: "outlet",
          cityId: o.city,
          districtId: o.district,
          countryId: o.country,
          cityName: o.cityName || "",
          districtName: o.districtName || "",
          address: o.address || "",
          shortAddress: o.address || "",
        }),
      );

    return [...warehouses, ...outlets];
  }, [warehouseData, outletData]);

  const watchedWarehouse = watch("warehouse");
  const watchedOutlet = watch("outlet");
  const watchedRoles = watch("roles");
  const watchedAssignWarehouse = watch("assignWarehouse");

  const selectedPrimaryLocation = watchedWarehouse || watchedOutlet || "";

  const selectedLocationOption = useMemo(
    () =>
      combinedLocationOptions.find(
        (opt) => String(opt.id) === String(selectedPrimaryLocation),
      ) ?? null,
    [combinedLocationOptions, selectedPrimaryLocation],
  );

  const activeLocationType: "outlet" | "warehouse" | null =
    selectedLocationOption?.type ?? null;

  const activeRoles: any[] = useMemo(() => {
    if (activeLocationType === "outlet") return outletRoles?.roles ?? [];
    return warehouseRoles?.roles ?? [];
  }, [activeLocationType, outletRoles, warehouseRoles]);

  useEffect(() => {
    if (!watchedRoles || activeRoles.length === 0) return;
    const stillValid = activeRoles.some(
      (r: any) => String(r.id) === String(watchedRoles),
    );
    if (!stillValid) {
      setValue("roles", "", { shouldValidate: false, shouldDirty: false });
    }
  }, [watchedRoles, activeRoles, setValue]);

  const assignLocationOptions = useMemo(
    () =>
      combinedLocationOptions
        .filter((o) => String(o.id) !== String(selectedPrimaryLocation))
        .map((o) => ({
          id: o.id,
          value: o.id,
          label: o.type === "outlet" ? `${o.name} (Outlet)` : o.name,
        })),
    [combinedLocationOptions, selectedPrimaryLocation],
  );

  const selectedRole = activeRoles.find(
    (r: any) => String(r.id) === String(watchedRoles),
  );

  // STEP 1: Scalar fields only — warehouse/outlet/role deferred to Steps 2 & 3
  useEffect(() => {
    if (!staffDetails) return;

    setValue("name", staffDetails?.name);
    setValue("phone", staffDetails?.phone);
    setValue("email", staffDetails?.email);
    setValue("city", staffDetails?.cityId);
    setValue("district", staffDetails?.districtId);
    setValue("supervisorId", staffDetails?.supervisorId);
    setValue("timeSlotId", staffDetails?.timeSlotId);
    setValue("address", staffDetails?.address);
    setValue("iqamaNo", staffDetails?.iqamaNo);
    setValue("shortAddress", staffDetails?.shortAddress);
    setValue("startDate", staffDetails?.startDate?.slice(0, 10));
    setValue("photo", staffDetails?.photo);
    setValue("iqamaDoc", staffDetails?.iqamaDoc);

    if (isDSClient) {
      setValue("country", staffDetails?.countryId);
      setValue("darkStoreId", staffDetails?.darkStoreId);
      setCreateLocation((prev: any) => ({
        ...prev,
        country: { id: staffDetails?.countryId, name: staffDetails?.country },
        city: { id: staffDetails?.cityId, name: staffDetails?.city },
        district: {
          id: staffDetails?.districtId,
          name: staffDetails?.district,
        },
      }));
    }

    setSelectedData({
      country: staffDetails?.countryId,
      city: staffDetails?.cityId,
      district: staffDetails?.districtId,
    });
  }, [staffDetails, isDSClient, setValue]);

  // STEP 2: Set warehouse/outlet only after combinedLocationOptions has loaded.
  useEffect(() => {
    if (!staffDetails || combinedLocationOptions.length === 0) return;

    const primaryWarehouse = staffDetails?.warehouseList?.[0];
    const primaryOutlet = staffDetails?.outletList?.[0];

    if (primaryWarehouse) {
      setValue("warehouse", primaryWarehouse.id, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue("outlet", "", { shouldValidate: false, shouldDirty: false });
    } else if (primaryOutlet) {
      setValue("outlet", primaryOutlet.id, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setValue("warehouse", "", { shouldValidate: false, shouldDirty: false });
    }

    const remainingWarehouses = (
      staffDetails?.warehouseList?.slice(primaryWarehouse ? 1 : 0) || []
    ).map((w: any) => ({ id: w.id, value: w.id, label: w.name }));

    const remainingOutlets = (
      staffDetails?.outletList?.slice(primaryOutlet ? 1 : 0) || []
    ).map((o: any) => ({ id: o.id, value: o.id, label: o.name }));

    setValue("assignWarehouse", [...remainingWarehouses, ...remainingOutlets]);
  }, [
    staffDetails,
    staffDetails?.id,
    combinedLocationOptions.length,
    setValue,
  ]);

  // STEP 3: Set role only after activeRoles has loaded.
  useEffect(() => {
    if (!staffDetails?.roleId || activeRoles.length === 0) return;

    const match = activeRoles.find(
      (r: any) => String(r.id) === String(staffDetails.roleId),
    );

    if (match) {
      setValue("roles", String(match.id), {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [
    staffDetails?.roleId,
    activeRoles,
    setValue,
    watchedWarehouse,
    watchedOutlet,
  ]);

  useEffect(() => {
    if (staffDetails?.roleId && !watchedRoles) return;

    if (!watchedRoles || activeRoles.length === 0) return;

    const stillValid = activeRoles.some(
      (r: any) => String(r.id) === String(watchedRoles),
    );

    if (!stillValid) {
      setValue("roles", "", {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [watchedRoles, activeRoles, setValue, staffDetails?.roleId]);

  const handlePrimaryLocationChange = (val: string) => {
    const option = combinedLocationOptions.find(
      (o) => String(o.id) === String(val),
    );
    if (!option) return;

    if (option.type === "warehouse") {
      setValue("warehouse", val, { shouldValidate: true });
      setValue("outlet", "", { shouldValidate: true });
    } else {
      setValue("outlet", val, { shouldValidate: true });
      setValue("warehouse", "", { shouldValidate: true });
    }

    setValue("city", option.cityId || "");
    setValue("district", option.districtId || "");
    setValue("country", option.countryId || "");
    setValue("address", option.address || "");
    setValue("shortAddress", option.shortAddress || "");

    setSelectedData((prev: any) => ({
      ...prev,
      [option.type]: val,
      country: option.countryId,
      city: option.cityId,
      district: option.districtId,
    }));

    try {
      const currentAssign: any[] = watch("assignWarehouse") || [];
      const filtered = currentAssign
        .filter((a: any) => String(a?.id ?? a?.value ?? a) !== String(val))
        .map((a: any) => {
          if (typeof a === "string") {
            const found = combinedLocationOptions.find(
              (o) => String(o.id) === String(a),
            );
            return { id: a, value: a, label: found?.name ?? a };
          }
          const id = a?.id ?? a?.value;
          const label =
            a?.label ??
            combinedLocationOptions.find((o) => String(o.id) === String(id))
              ?.name ??
            id;
          return { id, value: id, label };
        });
      setValue("assignWarehouse", filtered);
    } catch (e) {
      console.warn("Failed to update assignWarehouse when primary changed", e);
    }

    clearErrors("city");
    clearErrors("district");
    clearErrors("warehouse");
    clearErrors("outlet");
  };

  // Section header component
  const SectionHeader = ({
    icon: Icon,
    title,
  }: {
    icon: any;
    title: string;
  }) => (
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );

  return (
    <div className="h-[calc(100vh-10rem)] overflow-y-auto space-y-6 p-4 scrollbar-clean">
      {/* Personal Information Section */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader icon={IconUser} title="Personal Information" />

        <Controller
          control={control}
          name="photo"
          rules={{
            validate: (file: File | string | undefined) => {
              if (!file || typeof file === "string") return true;
              if (file.size > 1 * 1024 * 1024)
                return t("File size must be 1 MB or less");
              const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
              const fileObj = file instanceof File ? file : null;
              if (fileObj && !allowedTypes.includes(fileObj.type)) {
                if (!["jpeg", "png", "jpg"].includes(getFileExtension(fileObj)))
                  return t("Only JPG, JPEG, or PNG allowed");
              }
              return true;
            },
          }}
          render={({ field, fieldState }) => {
            const fileURL =
              field.value instanceof File
                ? URL.createObjectURL(field.value)
                : typeof field.value === "string" && field.value !== ""
                  ? field.value
                  : "/img/dummy-logo.png";

            return (
              <div className="flex items-center gap-5 p-3 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                <div className="relative group">
                  <img
                    src={fileURL}
                    alt={t("Staff Profile")}
                    className="w-20 h-20 rounded-xl object-cover border-2 border-[#FBD037]/50 shadow-md transition-all group-hover:scale-105"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-[#FBD037] rounded-full p-0.5">
                    <IconPhoto size={14} className="text-white" />
                  </div>
                </div>

                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      id="photo-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          field.onChange(file);
                          setSelectedDocument?.((prev: any) => ({
                            ...prev,
                            photo: true,
                          }));
                          clearErrors("photo");
                        }
                      }}
                    />
                    <label
                      htmlFor="photo-upload"
                      className="inline-flex items-center gap-2 text-[#212121] cursor-pointer border rounded-lg text-sm px-4 py-1.5 border-[#FBD037] bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-all duration-200 font-medium"
                    >
                      <IconCloudUpload size={16} stroke={1.5} />
                      {t("Upload Photo")}
                    </label>

                    {field.value && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-red-500 text-sm hover:underline transition"
                        onClick={() => {
                          field.onChange(undefined);
                          setSelectedDocument?.((prev: any) => ({
                            ...prev,
                            photo: false,
                          }));
                        }}
                      >
                        <IconTrash size={14} />
                        {t("Remove")}
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-400">
                    {t("Accepted formats: PNG, JPG (Max file size: 1 MB)")}
                  </p>

                  {fieldState.error && (
                    <p className="text-red-500 text-xs font-medium mt-1 flex items-center gap-1">
                      <span className="text-red-400">⚠</span>{" "}
                      {t(fieldState.error.message || "")}
                    </p>
                  )}
                </div>
              </div>
            );
          }}
        />
        <div className="grid grid-cols-2 gap-4">
          <NewinputField
            className="col-span-2"
            name="name"
            type="text"
            title={t("Full Name")}
            register={register}
            error={errors.name}
            validation={{
              required: t("Name is required"),
              pattern: {
                value: /^(?=.*[a-zA-Z\u0600-\u06FF])[a-zA-Z\u0600-\u06FF0-9\s-]+$/,
                message: t("Name must contain at least one letter"),
              },
              validate: (value: string) =>
                value.trim() ? true : t("Name cannot be empty or only spaces"),
            }}
          />

          <NewinputField
            name="phone"
            type="text"
            title={t("Mobile No")}
            register={register}
            error={errors.phone}
            validation={{
              required: t("Mobile number is required"),
              pattern: {
                value: /^\d{9,10}$/,
                message: t("Mobile number must be 9 or 10 digits"),
              },
              validate: (value: string) => {
                if (/^0+$/.test(value)) {
                  return t("Mobile number cannot be all zeros");
                }
                return true;
              },
            }}
            other={{
              maxLength: 10,
              inputMode: "numeric",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                setValue("phone", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              },
            }}
          />

          <NewinputField
            name="email"
            type="email"
            title={t("Email")}
            register={register}
            error={errors.email}
            validation={{
              required: t("Email is required"),
              setValueAs: (value: string) => value?.toLowerCase(),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("Please enter a valid email address"),
              },
            }}
          />

          {staffDetails && isClient && (
            <NewinputField
              className="col-span-2"
              name="password"
              type="password"
              title={t("Password")}
              placeholder={t("Leave blank to keep ...")}
              register={register}
              error={errors.password}
              validation={{
                required: false,
                minLength: {
                  value: 8,
                  message: t("Password must be at least 8 characters"),
                },
                maxLength: {
                  value: 16,
                  message: t("Password must not exceed 16 characters"),
                },
                validate: (value: string) => {
                  if (!value) return true;
                  if (!/[A-Z]/.test(value))
                    return t("Must contain one uppercase letter");
                  if (!/[a-z]/.test(value))
                    return t("Must contain one lowercase letter");
                  if (!/[0-9]/.test(value)) return t("Must contain one number");
                  if (!/[!@#$%^&*(),.?/":{}|<>]/.test(value))
                    return t("Must contain one special character");
                  return true;
                },
              }}
            />
          )}
        </div>
      </div>

      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader icon={IconBriefcase} title="Work Assignment" />
        <div className="grid grid-cols-2 gap-4">
          {!isDSClient ? (
            <>
              <input type="hidden" {...register("outlet")} />

              <NewSelectInput
                className=""
                name="warehouse"
                label={t("Warehouse / Outlet")}
                placeholder={t("Search location...")}
                selectOptions={combinedLocationOptions.map((opt) => ({
                  id: opt.id,
                  value: opt.id,
                  text:
                    opt.type === "outlet" ? `${opt.name} (${t("Outlet")})` : opt.name,
                }))}
                clearErrors={clearErrors}
                register={register}
                validation={{
                  validate: (value: string) => {
                    const outletVal = watch("outlet");
                    if (!value && !outletVal)
                      return t("Warehouse or Outlet is required");
                    return true;
                  },
                }}
                error={errors.warehouse || errors.outlet}
                value={watch("warehouse") || watch("outlet") || ""}
                setValue={handlePrimaryLocationChange}
                disabled={combinedLocationOptions.length === 0}
              />

              {/* <NewinputField
                    name="city"
                    type="text"
                    title="City"
                    placeholder="Riyadh"
                    register={register}
                    error={errors.city}
                    validation={{ required: "City is required" }}
                    disabled={true}
                    value={selectedLocationOption?.cityName ?? ""}
                  />

                  <NewinputField
                    name="district"
                    type="text"
                    title="District"
                    placeholder="Al Riyadh"
                    register={register}
                    error={errors.district}
                    validation={{ required: "District is required" }}
                    disabled={true}
                    value={selectedLocationOption?.districtName ?? ""}
                  /> */}
            </>
          ) : (
            <>
              <NewSelectInput
                className=""
                name="country"
                label={t("Country")}
                placeholder={t("Select Country")}
                selectOptions={countries.map((country: any) => ({
                  id: country.id,
                  value: country.id,
                  text: country.name,
                }))}
                register={register}
                clearErrors={clearErrors}
                error={errors.country}
                validation={{ required: t("Country is required") }}
                value={watch("country")}
                setValue={(value: any) => {
                  setCreateLocation((prev: any) => ({
                    ...prev,
                    country: {
                      id: value,
                      name: countries?.find((c: any) => c.id === value)?.name,
                    },
                    city: undefined,
                    district: undefined,
                  }));
                  clearErrors("country");
                  setValue("country", value);
                }}
              />
              <NewSelectInput
                className=""
                name="city"
                label={t("City")}
                placeholder={
                  isFetchingCities ? t("Loading cities...") : t("Select City")
                }
                selectOptions={cities.map((city: any) => ({
                  id: city.id,
                  value: city.id,
                  text: city.name,
                }))}
                register={register}
                clearErrors={clearErrors}
                validation={{ required: t("City is required") }}
                error={errors.city}
                value={watch("city")}
                setValue={(value: any) => {
                  setCreateLocation((prev: any) => ({
                    ...prev,
                    city: {
                      id: value,
                      name: cities.find((c: any) => c.id === value)?.name,
                    },
                    district: undefined,
                  }));
                  clearErrors("city");
                  setValue("city", value);
                }}
              />
              <NewSelectInput
                className=""
                name="district"
                label={t("District")}
                placeholder={
                  isFetchingDistricts
                    ? t("Loading districts...")
                    : t("Select District")
                }
                selectOptions={
                  districts.map((district: any) => ({
                    id: district.id,
                    value: district.id,
                    text: district.name,
                  })) || []
                }
                register={register}
                clearErrors={clearErrors}
                error={errors.district}
                validation={{ required: t("District is required") }}
                value={watch("district")}
                setValue={(value: any) => {
                  setCreateLocation((prev: any) => ({
                    ...prev,
                    district: {
                      id: value,
                      name: districts.find((d: any) => d.id === value)?.name,
                    },
                  }));
                  clearErrors("district");
                  setValue("district", value);
                }}
              />
            </>
          )}
          {isDSClient && (
            <NewSelectInput
              className=""
              name="darkStoreId"
              label={t("Select Dark Store")}
              placeholder=""
              selectOptions={
                darkStores?.map((darkStore: any) => ({
                  id: darkStore?.id,
                  value: darkStore?.id,
                  text: darkStore?.name,
                })) || []
              }
              clearErrors={clearErrors}
              register={register}
              validation={{ required: t("Dark Store is required") }}
              error={errors.darkStoreId}
              disabled={!darkStores || darkStores?.length === 0}
              value={watch("darkStoreId")}
              setValue={(val: string) => setValue("darkStoreId", val)}
            />
          )}
          {/* <NewinputField
            name="address"
            type="text"
            title="Address"
            className="flex items-center gap-2" 
            placeholder="Enter complete address..."
            register={register}
            disabled={!isDSClient}
            validation={{ required: "Address is required" }}
            {...(!isDSClient && {
              value: selectedLocationOption?.address ?? "",
            })}
          /> */}

          <NewSelectInput
            name="supervisorId"
            label={t("Supervisor")}
            className=""
            selectOptions={
              staffData?.map((supervisor: any) => ({
                id: supervisor.id,
                value: supervisor.id,
                text: supervisor.name,
              })) || []
            }
            clearErrors={clearErrors}
            register={register}
            disabled={!staffData || staffData.length === 0}
            value={watch("supervisorId")}
            setValue={(val: string) => setValue("supervisorId", val)}
          />

          <NewSelectInput
            name="timeSlotId"
            label={t("Time Slot")}
            className=""
            selectOptions={
              timeSlotData?.map((t: any) => ({
                id: t.id,
                value: t.id,
                text: t.slotName,
              })) || []
            }
            clearErrors={clearErrors}
            register={register}
            disabled={!timeSlotData || timeSlotData?.length === 0}
            value={watch("timeSlotId")}
            setValue={(val: string) => setValue("timeSlotId", val)}
          />

          <NewSelectInput
            name="roles"
            label={
              activeLocationType === "outlet"
                ? t("Assign Role (Outlet)")
                : t("Assign Role")
            }
            className=""
            selectOptions={activeRoles.map((r: any) => ({
              id: r?.id,
              value: r?.id,
              text: r?.roleName,
            }))}
            clearErrors={clearErrors}
            register={register}
            value={watch("roles")}
            setValue={(val: string) => setValue("roles", val)}
            validation={{ required: t("Staff role is required") }}
            error={errors.roles}
            disabled={activeRoles.length === 0}
          />

          <NewinputField
            name="startDate"
            className=""
            type="date"
            title={t("Start Date")}
            register={register}
            error={errors.startDate}
            validation={{
              required: t("Start Date is required"),
              validate: (value: string) => {
                const selectedDate = new Date(value);
                const today = new Date();
                selectedDate.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                if (selectedDate < today) return t("Past dates are not allowed");
                return true;
              },
            }}
            min={new Date().toISOString().split("T")[0]}
          />

          {selectedRole?.multipleAccessWarehouse &&
            assignLocationOptions.length > 0 && (
              <Controller
                name="assignWarehouse"
                control={control}
                rules={{
                  required: t("At least one warehouse or outlet is required"),
                }}
                render={({ fieldState }) => (
                  <MultiSelectInput
                    label={t("Assign Additional Warehouses / Outlets")}
                    name="assignWarehouse"
                    className="w-full col-span-2"
                    options={assignLocationOptions}
                    value={watchedAssignWarehouse || []}
                    onChange={(val: any) =>
                      setValue("assignWarehouse", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    error={fieldState.error}
                  />
                )}
              />
            )}
        </div>
      </div>

      {/* Documents Section */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <SectionHeader icon={IconId} title="Identification & Documents" />

        <div className="flex items-center gap-2">
          <NewinputField
            name="iqamaNo"
            type="number"
            className="w-full"
            title={t("Iqama Number")}
            register={register}
            error={errors.iqamaNo}
            validation={{
              required: t("Iqama number is required"),
              validate: (value: string) =>
                value.trim().length === 10
                  ? true
                  : t("Iqama number must be 10 digits"),
            }}
            other={{
              maxLength: 10,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                let value = e.target.value.replace(/\D/g, "");
                if (value.length > 10) value = value.slice(0, 10);
                setValue("iqamaNo", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              },
            }}
          />
          <Controller
            control={control}
            name="iqamaDoc"
            rules={{
              required: t("File is required"),
              validate: (file: File) => {
                if (!file) return t("Please select a file");
                if (file.size > 1 * 1024 * 1024)
                  return t("File size must be 1 MB or less");
                const allowedTypes = [
                  "image/jpeg",
                  "image/png",
                  "image/jpg",
                  "application/pdf",
                ];
                if (
                  file.type
                    ? !allowedTypes.includes(file.type)
                    : !["jpeg", "png", "jpg", "pdf"].includes(
                        getFileExtension(file as any),
                      )
                ) {
                  return t("Only JPG, JPEG, PNG, or PDF files are allowed");
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => {
              const fileName =
                field.value instanceof File
                  ? field.value.name
                  : typeof field.value === "string" && field.value
                    ? decodeURIComponent(field.value.split("/").pop() || "")
                    : "";

              const isUploaded = !!fileName && !fieldState.error;

              return (
                <div className="flex flex-col gap-1.5">
                  <p className="text-sm text-[#1E293B]">{t("Upload")}</p>

                  <input
                    type="file"
                    id="upload-id"
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        field.onChange(file);
                        setSelectedDocument?.((prev: any) => ({
                          ...prev,
                          iqamaDoc: true,
                        }));
                        clearErrors("iqamaDoc");
                      }
                    }}
                  />

                  <label
                    htmlFor="upload-id"
                    className={`flex items-center justify-center gap-2 py-2 px-4 border-2 rounded-xl cursor-pointer transition-all duration-200 w-full
                          ${
                            fieldState.error
                              ? "border-red-300 bg-red-50 hover:bg-red-100"
                              : isUploaded
                                ? "border-green-400 bg-green-50 hover:bg-green-100"
                                : "border-[#D2D2D2] bg-[#FBD037]/10 hover:bg-[#FBD037]/20"
                          }`}
                  >
                    <IconCloudUpload
                      stroke={1.5}
                      size={18}
                      className={
                        isUploaded ? "text-green-600" : "text-gray-500"
                      }
                    />
                  </label>

                  {fieldState.error && (
                    <p className="text-red-500 text-xs leading-tight flex items-center gap-1">
                      <span className="text-red-400">⚠</span>{" "}
                      {t(fieldState.error.message || "")}
                    </p>
                  )}

                  {/* {isUploaded && !fieldState.error && (
                      <p className="text-green-600 text-xs leading-tight flex items-center gap-1">
                        <IconCheck size={12} />
                        {fileName.length > 30
                          ? `${fileName.slice(0, 27)}...`
                          : fileName}
                      </p>
                    )} */}
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AddStaffModal;
